/**
 * @fileoverview Confirm Reduction Phase Component
 * @module ConfirmReductionPhase
 * 
 * Business Context:
 * - Second phase of the Build Your Case wizard
 * - Prominent display of reduction amount
 * - Consistent navigation pattern with other phases
 */

import React, { useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Calculator,
  Edit3,
  Check,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { AppealSummary } from '../BuildYourCaseWizard';

interface ConfirmReductionPhaseProps {
  summary: AppealSummary | null;
  confirmedReduction: number;
  onReductionChange: (amount: number) => void;
  onGenerate: () => void;
  onBack: () => void;
}

const ConfirmReductionPhase: React.FC<ConfirmReductionPhaseProps> = ({
  summary,
  confirmedReduction,
  onReductionChange,
  onGenerate,
  onBack
}) => {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(confirmedReduction.toString());

  const formatCurrency = (dollars: number): string => {
    return `$${dollars.toLocaleString()}`;
  };

  const handleEditSave = () => {
    const numValue = parseInt(editValue.replace(/[^0-9]/g, ''), 10);
    if (!isNaN(numValue) && numValue >= 0) {
      onReductionChange(numValue);
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setEditValue(confirmedReduction.toString());
    setIsEditing(false);
  };

  // Use target from reduction breakdown
  const suggestedAmount = summary?.reductionBreakdown?.target ?? 0;
  const isCustomAmount = confirmedReduction !== suggestedAmount;

  return (
    <div className="space-y-4">
      {/* Main Card */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-teal-600 px-6 py-8 text-center text-white">
          <h2 className="text-lg font-semibold mb-1">Your Reduction Request</h2>
          <p className="text-teal-100 text-sm">
            The amount you'll ask the assessor to reduce your property value
          </p>
        </div>

        {/* Amount */}
        <div className="px-6 py-8 text-center border-b border-slate-100">
          {isEditing ? (
            <div className="max-w-[200px] mx-auto">
              <div className="relative mb-4">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-slate-400">$</span>
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value.replace(/[^0-9]/g, ''))}
                  className="w-full text-2xl font-bold text-center py-2 pl-8 pr-3 border-2 border-teal-300 rounded-lg focus:border-teal-500 focus:outline-none"
                  autoFocus
                />
              </div>
              <div className="flex gap-2 justify-center">
                <button
                  onClick={handleEditCancel}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <button
                  onClick={handleEditSave}
                  className="p-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors"
                >
                  <Check className="w-5 h-5" />
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center gap-2">
                <p className="text-4xl font-bold text-slate-900">
                  {formatCurrency(confirmedReduction)}
                </p>
                <button
                  onClick={() => {
                    setEditValue(confirmedReduction.toString());
                    setIsEditing(true);
                  }}
                  className="p-1.5 text-slate-300 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                  title="Edit amount"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-slate-500 text-sm mt-1">reduction in assessed value</p>
              {isCustomAmount && (
                <p className="text-xs text-amber-600 mt-2">
                  Custom amount (suggested: {formatCurrency(suggestedAmount)})
                </p>
              )}
            </>
          )}
        </div>

        {/* Breakdown Toggle */}
        {summary?.reductionBreakdown && (
          <div>
            <button
              onClick={() => setShowBreakdown(!showBreakdown)}
              className="w-full px-6 py-3 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2 text-slate-600">
                <Calculator className="w-4 h-4" />
                <span className="text-sm font-medium">How we calculated this</span>
              </div>
              {showBreakdown ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>

            {showBreakdown && (
              <div className="px-6 pb-5 space-y-2">
                {/* Impact breakdown */}
                {(summary.reductionBreakdown.maintenanceImpact ?? 0) > 0 && (
                  <div className="bg-slate-50 rounded-lg p-3 flex justify-between text-sm">
                    <span className="text-slate-600">Deferred Maintenance Impact</span>
                    <span className="text-teal-600 font-medium">
                      {formatCurrency(summary.reductionBreakdown.maintenanceImpact)}
                    </span>
                  </div>
                )}
                {(summary.reductionBreakdown.flawImpact ?? 0) > 0 && (
                  <div className="bg-slate-50 rounded-lg p-3 flex justify-between text-sm">
                    <span className="text-slate-600">County Evidence Flaws</span>
                    <span className="text-teal-600 font-medium">
                      {formatCurrency(summary.reductionBreakdown.flawImpact)}
                    </span>
                  </div>
                )}
                
                {/* Three-tier strategy */}
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500 mb-2 font-medium">Negotiation Strategy</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
                      <p className="text-xs text-amber-600">Opening</p>
                      <p className="font-bold text-amber-700 text-sm">
                        {formatCurrency(summary.reductionBreakdown.opening ?? 0)}
                      </p>
                    </div>
                    <div className="bg-teal-50 border-2 border-teal-400 rounded-lg p-2 text-center">
                      <p className="text-xs text-teal-600">Target</p>
                      <p className="font-bold text-teal-700 text-sm">
                        {formatCurrency(summary.reductionBreakdown.target ?? 0)}
                      </p>
                    </div>
                    <div className="bg-slate-100 border border-slate-200 rounded-lg p-2 text-center">
                      <p className="text-xs text-slate-500">Walkaway</p>
                      <p className="font-bold text-slate-600 text-sm">
                        {formatCurrency(summary.reductionBreakdown.walkaway ?? 0)}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Confidence & Tax Savings */}
                <div className="mt-2 flex justify-between items-center text-xs text-slate-500">
                  <span>Confidence: {summary.reductionBreakdown.confidence?.toUpperCase() ?? 'N/A'}</span>
                  <span>Est. Tax Savings: {formatCurrency(summary.reductionBreakdown.taxSavings?.target ?? 0)}/yr</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation - Consistent with other phases */}
      <div className="bg-teal-600 rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-teal-100 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <div className="text-center flex-1 px-4">
            <p className="text-white font-medium">Ready to generate your script</p>
            <p className="text-teal-100 text-sm">We'll create talking points from your evidence</p>
          </div>
          <button
            onClick={onGenerate}
            className="px-5 py-2.5 bg-white text-teal-700 rounded-lg font-semibold hover:bg-teal-50 transition-colors flex items-center gap-2"
          >
            Continue
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmReductionPhase;
